import React from 'react';
import PropTypes from 'prop-types';
import { usePrefs } from 'wsm-common-data';
import StaffInfoButton from '../modal/StaffInfoButton';
import ContactFormButton from '../modal/ContactFormButton';
import { useIsVertical } from '../../hooks/useIsVertical';
import { useContactBtnLinks } from '../../hooks/useContactBtnLinks';
import { StaffMemberShape } from '../../global-constants';
import {
	safeObfuscateEmail,
	safeDeobfuscateEmail
} from '../../utils/emailObfuscation';

const ContactActions = ({ staffMember, isWithinModal, children }) => {
	const { useContactForm } = usePrefs();
	const { isVertical } = useIsVertical();

	const [firstButtonAttrs, firstButtonIconCls, callClickHandler] =
		useContactBtnLinks(isWithinModal, 0, 'call', staffMember.department);
	const [secondButtonAttrs, secondButtonIconCls, emailClickHandler] =
		useContactBtnLinks(isWithinModal, 1, 'email', staffMember.department);
	const [modalButtonAttrs, modalButtonIconCls, infoClickHandler] =
		useContactBtnLinks(isWithinModal, 2, 'info', staffMember.department);

	// Create custom email click handler that deobfuscates email and navigates
	const handleEmailClick = (event) => {
		event.preventDefault();

		// Call the original tracking handler first
		emailClickHandler(event);

		// Get the obfuscated email from the data attribute
		const obfuscatedEmail = event.currentTarget.getAttribute(
			'data-obfuscated-email'
		);
		if (obfuscatedEmail) {
			// Deobfuscate the email and create mailto link
			const actualEmail = safeDeobfuscateEmail(obfuscatedEmail);
			window.location.href = `mailto:${actualEmail}`;
		}
	};

	return (
		<div
			className={`d-flex ${
				isVertical
					? 'mt-auto align-items-center'
					: 'flex-grow-1 align-items-end'
			} justify-content-${
				isWithinModal || !isVertical ? 'start' : 'center'
			}`}
			style={{ columnGap: '8px' }}
		>
			{staffMember?.phone && (
				<a
					href={`tel:${staffMember.phone}`}
					{...firstButtonAttrs}
					onClick={callClickHandler}
				>
					<i className={firstButtonIconCls} />{' '}
					{isWithinModal && 'Call'}
				</a>
			)}
			{staffMember?.email && useContactForm === 'false' && (
				// eslint-disable-next-line jsx-a11y/anchor-is-valid
				<a
					onClick={handleEmailClick}
					href="#"
					data-obfuscated-email={safeObfuscateEmail(
						staffMember.email
					)}
					{...secondButtonAttrs}
				>
					<i className={secondButtonIconCls} />{' '}
					{isWithinModal && 'Email'}
				</a>
			)}
			{staffMember && useContactForm === 'true' && (
				<ContactFormButton
					btnAttrs={secondButtonAttrs}
					iconCls={secondButtonIconCls}
					isVertical={isVertical}
					trackingHandler={emailClickHandler}
					staffMember={staffMember}
					isWithinModal={isWithinModal}
				>
					{children}
				</ContactFormButton>
			)}

			{/* This button should never render inside modal. */}
			{!isWithinModal && staffMember?.bio && (
				<StaffInfoButton
					btnAttrs={modalButtonAttrs}
					iconCls={modalButtonIconCls}
					departmentId={staffMember.department}
					isVertical={isVertical}
					trackingHandler={infoClickHandler}
				>
					{children}
				</StaffInfoButton>
			)}
		</div>
	);
};

ContactActions.propTypes = {
	staffMember: PropTypes.shape(StaffMemberShape),
	isWithinModal: PropTypes.bool,
	children: PropTypes.node
};

export default ContactActions;
